import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Search, Trophy } from 'lucide-react';
import { useCategories } from '@/hooks/useCategories';
import { Card, Button, Loader } from '@/components/ui';

export function Home() {
  const { t } = useTranslation();
  const { data: categories, isLoading } = useCategories();

  return (
    <div className="relative z-10 space-y-8">
      {/* Hero Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center py-8"
      >
        <motion.div
          animate={{ rotate: [0, 5, -5, 0] }}
          transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
          className="text-6xl mb-4"
        >
          🦁
        </motion.div>
        <h1 className="text-4xl md:text-5xl font-bold text-gradient mb-4">
          {t('home.title')}
        </h1>
        <p className="text-gray-400 text-lg mb-8 max-w-2xl mx-auto">
          {t('home.subtitle')}
        </p>

        <div className="flex flex-wrap justify-center gap-4">
          <Link to="/search">
            <Button icon={<Search className="w-4 h-4" />}>
              {t('home.findAnimal')}
            </Button>
          </Link>
          <Link to="/achievements">
            <Button variant="secondary" icon={<Trophy className="w-4 h-4" />}>
              {t('common.achievements')}
            </Button>
          </Link>
        </div>
      </motion.div>

      {/* Categories */}
      <section>
        <h2 className="text-2xl font-bold mb-6">{t('home.categoriesTitle')}</h2>

        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader size="lg" text={t('home.loadingCategories')} />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            {categories?.map((category, index) => (
              <motion.div
                key={category.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Link to={`/category/${category.slug}`}>
                  <Card hover className="text-center h-full">
                    <span className="text-5xl block mb-3">{category.icon}</span>
                    <h3 className="font-semibold text-white mb-1">
                      {category.name}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {category.animals_count} {t('common.animals')}
                    </p>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </section>

      {/* Quick Actions */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="bg-gradient-to-br from-cosmic-purple/20 to-transparent">
          <div className="flex items-start gap-4">
            <span className="text-4xl">🎲</span>
            <div>
              <h3 className="text-xl font-bold mb-2">{t('home.randomAnimal')}</h3>
              <p className="text-gray-400 mb-4">
                {t('home.randomAnimalDesc')}
              </p>
              <Link to="/animal/random">
                <Button size="sm">{t('common.tryIt')}</Button>
              </Link>
            </div>
          </div>
        </Card>

        <Card className="bg-gradient-to-br from-star-gold/20 to-transparent">
          <div className="flex items-start gap-4">
            <span className="text-4xl">🏆</span>
            <div>
              <h3 className="text-xl font-bold mb-2">{t('home.collectStars')}</h3>
              <p className="text-gray-400 mb-4">
                {t('home.collectStarsDesc')}
              </p>
              <Link to="/achievements">
                <Button size="sm" variant="secondary">{t('home.myAchievements')}</Button>
              </Link>
            </div>
          </div>
        </Card>
      </section>
    </div>
  );
}
